import { useState } from "react";
import { CheckCircle2, MessageCircle, Calendar, BarChart3, FileText, Lock, UserRound } from "lucide-react";

const ANNUAL_MONTHS_BILLED = 9;

const sharedPaidFeatures = [
  "Textos de rappels pour les activités prévues et les travaux à venir",
  "Intégration et capture des agendas scolaires et des échéances",
  "Rappels dans l'application web et par courriel",
  "Suivi avancé des séances",
  "Rapport configurable pour les parents",
];

const plans = [
  {
    id: "essential",
    name: "Essentiel",
    monthly: 15,
    note: "Annulation en 1 clic, sans frais.",
    features: [
      "Coach d'organisation scolaire de base",
      "Accès pour 1 élève + 1 parent",
      "Plan d'étude hebdomadaire personnalisé",
      "Rappels dans l'application web et par courriel",
      "Suivi des séances",
      "Rapport d'activité",
    ],
    cta: "Commencer",
    highlight: false,
  },
  {
    id: "family",
    name: "Famille",
    monthly: 25,
    note: "Annulation en 1 clic, sans frais.",
    features: [
      "Coach d'organisation scolaire avancé",
      "Jusqu'à 5 usagers par famille (élèves et parents)",
      "Jusqu'à 25 plans d'étude pour tous les travaux et toutes les matières",
      ...sharedPaidFeatures,
    ],
    cta: "Choisir ce plan",
    highlight: true,
  },
  {
    id: "family-plus",
    name: "Famille Plus",
    monthly: 65,
    note: "Frais et forfaits applicables.",
    features: [
      "Coach d'organisation scolaire avancé avec assistant IA",
      "Interactions vocales avec l'app",
      "Jusqu'à 7 usagers par famille",
      "Jusqu'à 50 plans d'étude",
      "Rappels intelligents par téléphone, par texte et dans l'application web",
      {
        text: "Connexion optionnelle avec un accompagnateur IA",
        note: "Des limites de consommation s'appliquent.",
      },
      "Suivi avancé et tendances observées",
      "Rapport multiparties avec enseignants, orthopédagogues et tuteurs",
    ],
    cta: "Choisir ce plan",
    highlight: false,
  },
];

const bento = [
  {
    icon: MessageCircle,
    title: "Rappels",
    body: "Au bon moment, chaque jour.",
  },
  {
    icon: Calendar,
    title: "Plan personnalisé",
    body: "Adapté à votre enfant et à son horaire.",
  },
  {
    icon: BarChart3,
    title: "Suivi simple",
    body: "Oui/non, durée et commentaires.",
  },
  {
    icon: FileText,
    title: "Rapport hebdomadaire",
    body: "Voyez les progrès chaque semaine.",
  },
  {
    icon: Lock,
    title: "Données sécurisées",
    body: "Vos données et celles de votre enfant sont protégées.",
  },
  {
    icon: UserRound,
    title: "Personnalisation",
    body: "Avatar et expérience adaptés à votre enfant.",
  },
];

const Pricing = ({ onStartTrial }) => {
  const [annual, setAnnual] = useState(false);

  return (
    <section
      id="tarifs"
      data-testid="pricing-section"
      className="py-24 sm:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase font-bold tracking-[0.2em] text-[#189346]">
            Tarification
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#122118] leading-[1.1]">
            Des tarifs simples et accessibles
          </h2>
          <p className="mt-4 text-lg text-[#4a5f53]">
            Tout ce qu'il faut pour aider votre enfant à réussir.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <div
              className="inline-flex rounded-full bg-[#F3F6F4] p-1"
              role="group"
              aria-label="Période de facturation"
            >
              <button
                type="button"
                aria-pressed={!annual}
                data-testid="pricing-billing-monthly"
                onClick={() => setAnnual(false)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  annual ? "text-[#4a5f53]" : "bg-[#122118] text-white"
                }`}
              >
                Mensuel
              </button>
              <button
                type="button"
                aria-pressed={annual}
                data-testid="pricing-billing-annual"
                onClick={() => setAnnual(true)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  annual ? "bg-[#122118] text-white" : "text-[#4a5f53]"
                }`}
              >
                Annuel
              </button>
            </div>
            <p className="min-h-[1.25rem] text-sm text-[#4a5f53]">
              {annual
                ? "Prix de 9 mois pour 12 mois d'accès."
                : "Facturation chaque mois. Passez à l'annuel pour payer 9 mois."}
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {plans.map((plan, index) => {
            const amount = annual ? plan.monthly * ANNUAL_MONTHS_BILLED : plan.monthly;
            const period = annual ? "/ an" : "/ mois";
            return (
              <div
                key={plan.id}
                className="relative flex h-full flex-col rounded-3xl border-2 bg-[#F8FAF9] p-8"
                style={{ borderColor: plan.highlight ? "#25D366" : "#E5E7EB" }}
                data-testid={index === 0 ? "pricing-card" : `pricing-card-${plan.id}`}
              >
                <div
                  className="absolute -top-3 left-8 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
                  style={
                    plan.highlight
                      ? { background: "#25D366", color: "#0a2914" }
                      : { background: "#E9FBF0", color: "#189346" }
                  }
                >
                  {plan.name}
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-extrabold text-[#122118] sm:text-6xl">
                    {amount} $
                  </span>
                  <span className="text-[#4a5f53]">{period}</span>
                </div>
                <div className="mt-1 min-h-[2.5rem] text-sm text-[#4a5f53]">{plan.note}</div>

                <ul className="mt-7 flex-1 space-y-3">
                  {plan.features.map((feature) => {
                    const text = typeof feature === "string" ? feature : feature.text;
                    const note = typeof feature === "string" ? "" : feature.note;
                    return (
                      <li key={text} className="flex items-start gap-3">
                        <span
                          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                          style={{ background: "#E9FBF0", color: "#189346" }}
                        >
                          <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                        </span>
                        <span className="text-[#122118]">
                          {text}
                          {note ? "*" : ""}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-4 min-h-[2.75rem] space-y-1 text-right">
                  {plan.features.map((feature) =>
                    typeof feature !== "string" && feature.note ? (
                      <p key={feature.note} className="text-[11px] leading-snug text-[#829C8D]">
                        * {feature.note}
                      </p>
                    ) : null
                  )}
                </div>

                <button
                  type="button"
                  onClick={onStartTrial}
                  data-testid={
                    index === 0 ? "pricing-subscribe-button" : `pricing-subscribe-${plan.id}`
                  }
                  className="mt-8 inline-flex w-full items-center justify-center rounded-full px-7 py-4 text-base font-semibold transition-all hover:opacity-95"
                  style={{
                    background: "var(--sp-primary)",
                    color: "var(--sp-text-on-primary)",
                  }}
                >
                  {plan.cta}
                </button>

                <div className="mt-3 flex items-center justify-center gap-2 text-center text-sm text-[#4a5f53]">
                  <CheckCircle2 className="h-4 w-4 text-[#189346]" />
                  Essai gratuit de 30 jours
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-20">
          <h3 className="text-center font-display text-2xl font-bold text-[#122118] sm:text-3xl">
            Inclus dans tous les plans
          </h3>
          <div
            className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3"
            data-testid="pricing-bento"
          >
            {bento.map((b) => (
              <div
                key={b.title}
                className="flex h-full min-h-[168px] flex-col rounded-2xl border border-gray-100 bg-white p-6 transition-shadow hover:shadow-md"
                data-testid={`bento-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{ background: "#E9FBF0", color: "#189346" }}
                >
                  <b.icon className="h-5 w-5" strokeWidth={2.2} />
                </span>
                <h4 className="mt-4 font-display text-lg font-semibold text-[#122118]">
                  {b.title}
                </h4>
                <p className="mt-1 text-sm leading-relaxed text-[#4a5f53]">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
